import { Command, LogIn, Menu, UserRound, Users, X } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { Logo } from './Logo'
import { OpenSourceLinks } from './OpenSourceLinks'
import { useI18n } from '../i18n'
import { api } from '../lib/api'
import { CommandPalette } from './CommandPalette'

function PortfolioLink({ href, label, active, mobile = false, onClick }: { href: string; label: string; active?: boolean; mobile?: boolean; onClick?: () => void }) {
  const className = mobile
    ? `block border-b border-ink/10 py-3 text-sm ${active ? 'font-semibold text-moss' : 'text-ink/65'}`
    : `border-b py-2 text-[12px] font-medium transition duration-200 ${active ? 'border-moss text-ink' : 'border-transparent text-ink/68 hover:border-ink/28 hover:text-ink'}`

  if (href.startsWith('#') || href.startsWith('/#')) {
    return <a href={href} className={className} onClick={onClick}>{label}</a>
  }
  return <Link href={href} className={className} onClick={onClick}>{label}</Link>
}

export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [commandOpen, setCommandOpen] = useState(false)
  const [authenticated, setAuthenticated] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [location] = useLocation()
  const { locale, t, toggleLocale } = useI18n()
  const isHome = location === '/'
  const portfolioLinks = [
    { href: '/', label: locale === 'zh-CN' ? '首页' : 'Index', active: isHome },
    { href: '/chat', label: locale === 'zh-CN' ? '访谈' : 'Interviews', active: location === '/chat' },
    { href: '/resume', label: locale === 'zh-CN' ? '履历' : 'Resume', active: location === '/resume' },
  ]

  useEffect(() => {
    const refresh = () => api.session().then((session) => setAuthenticated(session.authenticated)).catch(() => setAuthenticated(false))
    void refresh()
    window.addEventListener('agentme-auth-changed', refresh)
    return () => window.removeEventListener('agentme-auth-changed', refresh)
  }, [])

  useEffect(() => setOpen(false), [location])

  useEffect(() => {
    const updateHeader = () => {
      setScrolled(window.scrollY > 32)
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      setScrollProgress(scrollable > 0 ? Math.min(1, window.scrollY / scrollable) : 0)
    }
    updateHeader()
    window.addEventListener('scroll', updateHeader, { passive: true })
    window.addEventListener('resize', updateHeader)
    return () => {
      window.removeEventListener('scroll', updateHeader)
      window.removeEventListener('resize', updateHeader)
    }
  }, [location])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setCommandOpen((value) => !value)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const homeHeaderAtTop = isHome && !scrolled

  return (
    <div className="min-h-screen bg-paper text-ink">
      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
      <header className={`fixed inset-x-0 top-0 z-50 border-b border-ink/12 text-ink backdrop-blur-xl transition-colors duration-300 ${homeHeaderAtTop ? 'bg-white/88' : 'bg-white/96 shadow-[0_6px_24px_rgba(16,47,80,.04)]'}`}>
        <div className={`mx-auto flex h-[72px] items-center justify-between px-5 sm:px-8 ${isHome ? 'max-w-[1240px] lg:px-10' : 'max-w-[1440px] lg:px-12'}`}>
          {isHome ? (
            <Link href="/" className="inline-flex items-center gap-2.5" aria-label={locale === 'zh-CN' ? '划水的周某首页' : 'John Zhou home'}>
              <img src="/common-use/头像.png" alt="" width={30} height={30} className="size-[30px] rounded-full border border-ink/18 object-cover grayscale" />
              <span className="text-[13px] font-semibold tracking-[-0.02em]">{locale === 'zh-CN' ? '划水的周某' : 'John Zhou'}</span>
            </Link>
          ) : <Logo />}
          <nav className="hidden items-center gap-6 lg:flex" aria-label={t('nav.main')}>
            {portfolioLinks.map((link) => <PortfolioLink key={`${link.href}-${link.label}`} {...link} />)}
            <Link
              href="/human-api"
              data-testid="top-human-api-link"
              className="inline-flex items-center gap-1.5 border border-ink/14 bg-ink px-3.5 py-2 text-[12px] font-bold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-moss focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-moss"
            >
              <Users size={13} aria-hidden="true" /> {t('nav.humanApi')}
            </Link>
          </nav>
          <div className="hidden items-center gap-2 lg:flex">
            <button type="button" onClick={() => setCommandOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-ink/14 bg-white px-3 py-2 text-[10px] font-semibold text-ink/62 transition hover:border-moss/45 hover:text-moss" aria-label={locale === 'zh-CN' ? '打开命令面板' : 'Open command palette'}>
              <Command size={12} /><kbd className="font-mono">⌘ K</kbd>
            </button>
            <button type="button" onClick={toggleLocale} className="border border-ink/16 px-3 py-2 text-[10px] font-semibold text-ink/68 transition hover:border-ink/32 hover:bg-white" aria-label={t('language.switch')}>
              {locale === 'en' ? '中文' : 'EN'}
            </button>
            <Link href="/account" className="inline-flex items-center gap-2 bg-ink px-4 py-2.5 text-[12px] font-semibold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-moss">
              {authenticated ? <UserRound size={13} /> : <LogIn size={13} />}
              {t(authenticated ? 'nav.myAccount' : 'nav.signIn')}
            </Link>
          </div>
          <button
            className="grid size-10 place-items-center border border-ink/18 lg:hidden"
            onClick={() => setOpen((value) => !value)}
            aria-label={t('nav.toggle')}
            aria-expanded={open}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-ink/12 bg-paper px-5 py-4 lg:hidden" aria-label={t('nav.mobile')}>
            {portfolioLinks.map((link) => <PortfolioLink key={`${link.href}-${link.label}`} {...link} mobile onClick={() => setOpen(false)} />)}
            <Link href="/human-api" data-testid="top-human-api-mobile-link" onClick={() => setOpen(false)} className="mt-4 flex items-center justify-center gap-2 border border-ink/14 bg-ink px-4 py-3 text-sm font-bold text-white hover:bg-moss">
              <Users size={15} aria-hidden="true" /> {t('nav.humanApi')}
            </Link>
            <Link href="/account" onClick={() => setOpen(false)} className="mt-3 flex items-center justify-center gap-2 bg-ink px-4 py-3 text-sm font-bold text-white">
              {authenticated ? <UserRound size={15} /> : <LogIn size={15} />}
              {t(authenticated ? 'nav.myAccount' : 'nav.signIn')}
            </Link>
            <button type="button" onClick={toggleLocale} className="mt-3 w-full border border-ink/16 px-3 py-2 text-xs font-semibold">
              {locale === 'en' ? '中文' : 'English'}
            </button>
            <button type="button" onClick={() => { setOpen(false); setCommandOpen(true) }} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-ink/16 bg-white px-3 py-3 text-xs font-semibold text-ink">
              <Command size={14} /> {locale === 'zh-CN' ? '命令面板' : 'Command palette'} <kbd className="font-mono text-[9px] text-ink/45">⌘K</kbd>
            </button>
          </nav>
        )}
        <span aria-hidden="true" className="absolute -bottom-px left-0 h-0.5 bg-moss transition-[width] duration-150" style={{ width: `${scrollProgress * 100}%` }} />
      </header>
      <main key={location} className={`${isHome ? '' : 'pt-[72px]'} page-enter`}>{children}</main>
      <footer className="border-t border-ink/12 bg-[#edf3f7]">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-3 px-5 py-5 text-[10px] text-ink/65 sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-12">
          <span>© {new Date().getFullYear()} Agent-Me / jz-ai-chat.com</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <a href="mailto:johnzhou811@gmail.com" className="font-semibold text-moss underline-offset-4 hover:underline">johnzhou811@gmail.com</a>
            <Link href="/contact" className="font-semibold text-moss underline-offset-4 hover:underline">{t('contact.link')}</Link>
            <OpenSourceLinks compact />
            <Link href="/privacy" className="font-semibold text-moss underline-offset-4 hover:underline">{t('nav.privacyFooter')}</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
